import { useEffect, useRef, useCallback } from 'react';

const IdleTimer = ({ timeout = 300000, onIdle }) => {
    const lastActiveRef = useRef(Date.now());
    const timerRef = useRef(null);
    // Store onIdle in a ref so the effect doesn't re-run when the callback changes
    const onIdleRef = useRef(onIdle);

    // Keep the ref in sync with the latest callback
    useEffect(() => {
        onIdleRef.current = onIdle;
    }, [onIdle]);

    const resetTimer = useCallback(() => {
        lastActiveRef.current = Date.now();
    }, []);

    useEffect(() => {
        // Reset on mount
        lastActiveRef.current = Date.now();

        const events = [
            'mousemove',
            'mousedown',
            'keydown',       // modern replacement for keypress
            'keypress',      // keep for backward compatibility
            'DOMMouseScroll',
            'mousewheel',
            'touchmove',
            'touchstart',    // catch initial touch
            'MSPointerMove',
            'scroll',
            'click',
            'input',         // catch typing in input fields
            'focus',         // catch tab-switching between inputs
            'pointerdown',   // catch pointer events
        ];

        events.forEach(event => {
            window.addEventListener(event, resetTimer, { passive: true, capture: true });
        });

        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActiveRef.current;
            if (elapsed >= timeout) {
                // Clear the interval FIRST to prevent repeated firings
                clearInterval(timerRef.current);
                timerRef.current = null;
                if (onIdleRef.current) onIdleRef.current();
            }
        }, 5000); // Check every 5 seconds (no need for 1s precision)

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, resetTimer, { capture: true });
            });
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [timeout, resetTimer]); // onIdle is NOT in deps — we use the ref

    return null;
};

export default IdleTimer;
